import { useCallback, useEffect, useMemo, useState } from "react";
import { useProjectParams } from "~/helpers";
import { AddToVersionResponse } from "~/lib/wss-client";
import { useWebsocket } from "./useWebsocket";

type UseWebSocketAddToVersionReturn = {
  addToVersion: (
    sourceVolumeId: string,
    targetVolumeId: string,
    documentIds: string[]
  ) => void;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
};

type UseWebSocketAddToVersionOptions = {
  onAddToVersionResponse?: (response: AddToVersionResponse) => void;
};

export const useWebSocketAddToVersion = (
  options?: UseWebSocketAddToVersionOptions
): UseWebSocketAddToVersionReturn => {
  const { organizationId, caseId } = useProjectParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsParams = useMemo(
    () => ({
      projectId: caseId,
      subscribeTo: "add-to-version" as const,
    }),
    [caseId]
  );

  const {
    isConnected,
    addToVersionListener,
    removeToVersionListener,
    sendAddToVersion,
  } = useWebsocket<AddToVersionResponse>(wsParams);

  const handleAddToVersionResponse = useCallback(
    (response: AddToVersionResponse) => {
      setIsLoading(false);

      if (response.action === "message-error") {
        setError(response.error || "Unknown error occurred");
      } else if (response.action === "message-response") {
        setError(null);
      }
      // Call external handler if provided
      options?.onAddToVersionResponse?.(response);
    },
    [options]
  );

  useEffect(() => {
    addToVersionListener(handleAddToVersionResponse);
    return () => {
      removeToVersionListener(handleAddToVersionResponse);
    };
  }, [
    addToVersionListener,
    removeToVersionListener,
    handleAddToVersionResponse,
  ]);

  const addToVersion = useCallback(
    (sourceVolumeId: string, targetVolumeId: string, documentIds: string[]) => {
      if (!isConnected) {
        setError("WebSocket is not connected");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        sendAddToVersion(
          organizationId,
          sourceVolumeId,
          targetVolumeId,
          documentIds
        );
      } catch (err) {
        setIsLoading(false);
        setError(
          err instanceof Error ? err.message : "Failed to add to version"
        );
      }
    },
    [isConnected, organizationId, sendAddToVersion]
  );

  return {
    addToVersion,
    isConnected,
    isLoading,
    error,
  };
};
